import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Card from '../components/Card';
import Modal from '../components/Modal';
import ErrorMessage from '../components/ErrorMessage';
import SearchInput from '../components/SearchInput';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

const Users: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role !== 'superAdmin') {
      navigate('/');
      return;
    }

    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
      setError(null);
    } catch (error) {
      logger.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserUpdate = async (userId: string, role: string, isActive: boolean) => {
    try {
      const response = await api.patch(`/users/${userId}`, { role, isActive });
      if (response.status === 200) {
        setUsers(users.map(user => 
          user._id === userId ? { ...user, role: response.data.role, isActive: response.data.isActive } : user
        ));
      }
    } catch (error) {
      logger.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await api.delete(`/users/${selectedUser._id}`);
      setUsers(users.filter(user => user._id !== selectedUser._id));
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      logger.error('Error deleting user:', error);
    }
  };

  return (
    <div className="py-6">
      <div className="w-full px-6">
        <Card>
          <div className="px-6 py-5 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <SearchInput
                onSearch={setSearchQuery}
                placeholder="Search users..."
              />
            </div>

            {error && (
              <ErrorMessage
                message={error}
                className="mb-6"
                onDismiss={() => setError(null)}
              />
            )}

            {isLoading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-gray-100 p-4 rounded-lg">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/4 mt-2"></div>
                  </div>
                ))}
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No users found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {users
                  .filter(user =>
                    user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    user.role.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((user) => (
                    <div
                      key={user._id}
                      className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="p-4 sm:p-6">
                        <div className="flex items-start justify-between space-x-4">
                          <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <span className="text-gray-500 font-medium">
                                    {user.firstName[0]}
                                    {user.lastName[0]}
                                  </span>
                                </div>
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-lg font-medium text-gray-900 truncate">
                                  {user.firstName} {user.lastName}
                                </h3>
                                <p className="text-sm text-gray-500 truncate">{user.email}</p>
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.role === 'superAdmin' ? 'bg-purple-100 text-purple-800' :
                                user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {user.role}
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {user.isActive ? 'Active' : 'Inactive'}
                              </span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-500 bg-gray-100">
                                Joined {new Date(user.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                setSelectedUser(user);
                                setIsEditModalOpen(true);
                              }}
                            >
                              Edit
                            </Button>
                            {user._id !== user?._id && (
                              <Button
                                variant="danger"
                                size="small"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setIsDeleteModalOpen(true);
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Card>

        {/* Edit User Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
          }}
          title="Edit User"
          actions={{
            primary: {
              label: 'Save Changes',
              onClick: () => {
                if (selectedUser) {
                  handleUserUpdate(selectedUser._id, selectedUser.role, selectedUser.isActive);
                  setIsEditModalOpen(false);
                }
              }
            },
            secondary: {
              label: 'Cancel',
              onClick: () => {
                setIsEditModalOpen(false);
              }
            }
          }}
          afterLeave={() => {
            setSelectedUser(null);
          }}
        >
          {selectedUser && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  value={selectedUser.role}
                  onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="superAdmin">Super Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      id="toggle"
                      className="sr-only"
                      checked={selectedUser.isActive}
                      onChange={(e) => setSelectedUser({ ...selectedUser, isActive: e.target.checked })}
                    />
                    <div className={`block h-6 rounded-full transition duration-150 ${selectedUser.isActive ? 'bg-green-400' : 'bg-gray-300'}`}>
                      <div className={`absolute left-0 top-0 w-4 h-4 mt-1 ml-1 bg-white rounded-full transition-transform duration-150 transform ${selectedUser.isActive ? 'translate-x-4' : ''}`}></div>
                    </div>
                  </div>
                  <label htmlFor="toggle" className="text-sm text-gray-700">
                    {selectedUser.isActive ? 'Active' : 'Inactive'}
                  </label>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
          }}
          title="Delete User"
          actions={{
            primary: {
              label: 'Delete',
              onClick: handleDeleteUser
            },
            secondary: {
              label: 'Cancel',
              onClick: () => {
                setIsDeleteModalOpen(false);
              }
            }
          }}
          afterLeave={() => {
            setSelectedUser(null);
          }}
        >
          <div className="text-center sm:mt-5">
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete {selectedUser?.firstName} {selectedUser?.lastName}? This action cannot be undone.
              </p>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Users; 